import { useEffect, useMemo, useState } from "react";
import { Row, Col, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { confirmAlert } from '~/components/confirmAlert/confirmAlert';
import { inject } from 'mobx-react';

//components
import withRemoveDialog from '~/hoc/withRemoveDialog';
import TimePickerInput from '~/components/TimePicker';
import InputTime from '~/components/GenericForm/Widgets/InputTime';
import Button from '~/components/CustomButton/CustomButton';
import TableWidget from '../../TableWidget.js';
import OvertimeMapper from '../OvertimeMapper.js'
import WagetypeRefetch from '../../WagetypeRefetch.js';

//elements
import Select from '~/elements/Select.js';
import SwitchWidget from '../../SwitchWidget.js';
import RequiredStar from '~/elements/RequiredStar.js';
import { Input } from "~/elements/Input";
import { NumberInput } from "../../../../../elements/Input";
import LoadingSpinner from '~/elements/LoadingSpinner';

//utils
import { getMinutesFromTimeInput, minutesToTimeInput } from '~/utils/timeCalc';
import { validateRequestPayload, validateStartAndEndTime } from '~/utils/commonMethods';
import { timeFormat, totalTimeFormat } from '~/utils/dateFormat';
import { workTypes } from '~/utils/constants.js';
import { FCODE_RULES } from "~/utils/constants.js";

const breakMinOptions = [{ value: 0, label: '0 minute' }, { value: 30, label: '30 minutes' },
{ value: 60, label: '60 minutes' }, { value: 90, label: '90 minutes' }];


const shiftType = [
    { value: 'dayShift', label: 'Day Shift' },
    { value: 'afternoonShift', label: 'Afteroon Shift' },
    { value: 'nightShift', label: 'Night Shift' },
];

const formatData = (data, dateTimeRules) => {
    const formattedData = [];
    data?.forEach(e => {
        formattedData.push({
            name: e.Overtime_Setting.name,
            from: timeFormat(e.from, dateTimeRules),
            to: timeFormat(e.to, dateTimeRules)
        })
    })
    return formattedData;
}

const hasDataChanged = (prev, current, isOvertimeMode) => {
    if (isOvertimeMode) {
        return (prev.maximum_work_limit_in_day !== getMinutesFromTimeInput(current.maximumWorkLimitInDay)
            || prev.overtime_threshold !== current.overtimeThreshold);
    } else {
        return ((prev.start_time !== getMinutesFromTimeInput(current.startTime))
            || (prev.end_time !== getMinutesFromTimeInput(current.endTime))
            || (prev.break_time_minutes !== current.breakTimeMinutes)
            || (prev.buffer_time !== getMinutesFromTimeInput(current.bufferTime))
        )
    }
}

const AddShift = ({ val, overtimeSettingList, updateData, cancel, t, dateTimeRules, commonStore, clientStore, handleDelete, overtimeEditMode = false, codeType = null }) => {
    const [shiftName, setShiftName] = useState(val?.shift_name);
    const [startTime, setStartTime] = useState(minutesToTimeInput(val?.start_time || 0));
    const [endTime, setEndTime] = useState(minutesToTimeInput(val?.end_time || 0));
    const [breakTimeMinutes, setBreakTimeMinutes] = useState(!isNaN(val.break_time_minutes) ? val.break_time_minutes : 0);
    const [bufferTime, setBufferTime] = useState(minutesToTimeInput(val?.buffer_time || 0));
    const [maximumWorkLimitInDay, setMaximumWorkLimitInDay] = useState(minutesToTimeInput(val?.maximum_work_limit_in_day || 0));
    const [overtimeThreshold, setOvertimeThreshold] = useState(val.overtime_threshold ? val.overtime_threshold : 15);
    const [companyShiftOvertimes, setCompanyShiftOvertimes] = useState(val?.Company_Shift_Overtimes || [])
    const [currentOvertimeSetting, setCurrentOvertimeSetting] = useState(null);
    const [showOvertimeForm, setShowOvertimeForm] = useState(false);
    const [isOvertimeEditMode, setIsOvertimeEditMode] = useState(overtimeEditMode);
    const [defaultOvertimeId, setDefaultOvertimeId] = useState(val?.default_overtime_id || null);
    const [associatedShift, setAssociatedShift] = useState(val?.shift_type || '');
    const [shortShiftCode, setShortShiftCode] = useState(val?.short_code || '');
    const [shiftColor, setShiftColor] = useState(val?.shift_color || '');
    const [isActive, setIsActive] = useState(val?.status === 'active');
    const [isLoading, setLoading] = useState(false);
    const [wageType, setWageType] = useState(commonStore.getWageTypesAccordingToIntegrations(commonStore?.config?.integration_details, 'su') || []);
    const [lastUpdated, setLastUpdateOn] = useState(commonStore.getWageTypesAccordingToIntegrations(commonStore?.config?.integration_details, 'last_updated') || null);
    const [isLoadingWageTypes, setIsLoadingWageTypes] = useState(false);
    const [payrollWageType, setPayrollWageType] = useState(val?.integrated_wagetype || null);

    // standby codes
    const [factor, setFactor] = useState(val?.factor || 1);
    const [workType, setWorkType] = useState(val?.work_type || null);

    const isFCode = val?.shift_type === 'freeShift';
    const isStandardShift = codeType === 'shiftCodes';

    const normalWorkDay = useMemo(() => {
        if (!startTime || !endTime) return "00:00";
        const startTotalMinutes = getMinutesFromTimeInput(startTime);
        const endTotalMinutes = getMinutesFromTimeInput(endTime);

        const totalTime = endTotalMinutes < startTotalMinutes ?
            endTotalMinutes + 1440 - startTotalMinutes - breakTimeMinutes :
            endTotalMinutes - startTotalMinutes - breakTimeMinutes;

        return minutesToTimeInput(totalTime);
    }, [startTime, endTime, breakTimeMinutes]);

    useEffect(() => {
        if (codeType === 'standbyCodes') {
            setShiftColor('#61b3ff');
        }
    }, [])

    const refetchWageType = async () => {
        setIsLoadingWageTypes(true);
        try {
            await commonStore.refetchWagetypes();
            commonStore.addNotification(t('Successfully refetched wagetypes'), null, 'success');
            setWageType(commonStore.getWageTypesAccordingToIntegrations(commonStore?.config?.integration_details, 'su') || []);
            setLastUpdateOn(commonStore.getWageTypesAccordingToIntegrations(commonStore?.config?.integration_details, 'last_updated') || null);
        } catch (err) {
            console.error(err);
            commonStore.addNotification(t('Something went wrong'), null, 'error');
        } finally {
            setIsLoadingWageTypes(false);
        }
    }

    const saveData = (saveAndReload = false) => {
        const __bufferTime = getMinutesFromTimeInput(bufferTime);
        const __normalWorkDay = getMinutesFromTimeInput(normalWorkDay);
        let __maximumWorkLimitInDay = getMinutesFromTimeInput(maximumWorkLimitInDay);
        const __startTime = getMinutesFromTimeInput(startTime);
        const __endTime = getMinutesFromTimeInput(endTime);

        const timeValidation = validateStartAndEndTime(__startTime, __endTime);
        if (timeValidation.hasError) {
            return updateData({ hasError: true, errors: [{ error: timeValidation.error }] });
        }

        if (__bufferTime > __normalWorkDay)
            return updateData({ hasError: true, errors: [{ "error": "Buffer time should be less then equal to normal work day" }] });

        if (__maximumWorkLimitInDay && __maximumWorkLimitInDay < __normalWorkDay) {
            if (isOvertimeEditMode)
                return updateData({ hasError: true, errors: [{ "error": "The daily work hours limit cannot be less than standard work hours" }] });
            else
                __maximumWorkLimitInDay = __normalWorkDay;
        }

        if (factor < 1 || !Number.isInteger(parseInt(factor)))
            return updateData({ hasError: true, errors: [{ "error": "Factor should be a positive number" }] });

        const shift = {
            id: val?.id || null,
            shift_name: shiftName,
            start_time: __startTime,
            end_time: __endTime,
            break_time_minutes: breakTimeMinutes,
            buffer_time: __bufferTime,
            normal_work_day: __normalWorkDay,
            maximum_work_limit_in_day: __maximumWorkLimitInDay ? __maximumWorkLimitInDay : __normalWorkDay,
            overtime_threshold: overtimeThreshold,
            shift_type: isStandardShift ? associatedShift : isFCode ? 'freeShift' : 'standbyShift',
            short_code: shortShiftCode,
            shift_color: shiftColor,
            status: isActive ? 'active' : 'inactive',
            default_overtime_id: defaultOvertimeId,
            work_type: workType,
            factor: factor,
            integrated_wagetype: payrollWageType
        }

        const validFields = ["shift_name", "maximum_work_limit_in_day"];
        const validMessage = ["Shift Name", "Maximum Work Limit In Day"];
        if (!isStandardShift && !isFCode) {
            validFields.push("work_type");
            validMessage.push("Work Type");
        }
        const errors = validateRequestPayload(shift, validFields, validMessage, t);

        if (errors)
            return updateData({ hasError: true, errors: errors });
        updateData(shift, saveAndReload, codeType);
    }

    const checkIfDataModified = (cb, param) => {
        const isDataModified = hasDataChanged(val, {
            startTime, endTime, breakTimeMinutes,
            bufferTime, maximumWorkLimitInDay, overtimeThreshold
        }, isOvertimeEditMode);
        if (isDataModified) {
            confirmAlert({
                title: t('Unsaved changes!'),
                message: t('Current changes are not saved, please save and then switch edit mode'),
                buttons: [
                    {
                        label: t('Save changes'),
                        onClick: () => saveData(true)
                    },
                    {
                        label: t('Cancel'),
                        onClick: cancel
                    }
                ],
            });
        } else {
            cb(param);
        }
    }

    const handleCompanyShiftOvertimeDelete = (idx) => {
        if (!isOvertimeEditMode)
            return commonStore.addNotification(t('Please enable overtime editing first'), null, 'warning');
        const __currentOvertimeSetting = companyShiftOvertimes[idx];
        if (!__currentOvertimeSetting || !__currentOvertimeSetting.id) return;
        setLoading(true);
        deleteOvertime(__currentOvertimeSetting.id);
    }

    const deleteOvertime = (id) => {
        handleDelete(id, async (id) => {
            try {
                await clientStore.deleteCompanyShiftOvertime(id);
                const __companyShiftOvertimes = companyShiftOvertimes.filter(e => e.id !== id);
                setCompanyShiftOvertimes(__companyShiftOvertimes);
                setLoading(false);
            } catch (err) {
                setLoading(false);
                console.error(err)
            }
        }, null, () => {
            setLoading(false);
        })
    }

    const handleCompanyShiftOvertimeForm = (index, isAdd = false) => {
        if (!isOvertimeEditMode)
            return commonStore.addNotification(t('Please enable overtime editing first'), null, 'warning');
        if (index === null && !isAdd) {
            setCurrentOvertimeSetting(null);
            setShowOvertimeForm(false);
        }
        else {
            const data = {
                id: null,
                overtime_setting_id: null,
                from: 0,
                to: 0
            };
            if (!isAdd) {
                const _currentShiftOvertime = companyShiftOvertimes[index];
                data.id = _currentShiftOvertime.id;
                data.overtime_setting_id = _currentShiftOvertime.overtime_setting_id;
                data.from = _currentShiftOvertime.from
                data.to = _currentShiftOvertime.to
            }
            setCurrentOvertimeSetting(data)
            setShowOvertimeForm(true);
        }
    }

    const handleCompanyShiftOvertime = (data) => {
        try {
            if (data.hasError)
                return commonStore.addNotification(t(data.errors[0].error), null, 'warning');
            data.company_shift_id = val.id;
            setLoading(true);
            saveShiftOvertime(data);
        } catch (err) {
            return commonStore.addNotification(t(err.message || 'Something went wrong!'), null, 'error');
        }
    }

    const saveShiftOvertime = async (data) => {
        try {
            let result;
            if (data.id)
                result = await clientStore.updateCompanyShiftOvertime(data.id, data);
            else
                result = await clientStore.saveCompanyShiftOvertime(data);
            const { companyShiftOvertime } = result;
            const __companyShiftOvertimes = companyShiftOvertimes.filter(e => e.id !== companyShiftOvertime.id);
            __companyShiftOvertimes.push(companyShiftOvertime);
            setCompanyShiftOvertimes(__companyShiftOvertimes);
            setLoading(false);
            handleCompanyShiftOvertimeForm(null, null);
            return commonStore.addNotification(t('Successfully added overtime'), null, 'success');
        } catch (err) {
            setLoading(false);
            return commonStore.addNotification(t(err.message || 'Something went wrong!'), null, 'error');
        }
    }

    const handleModeChange = (isOvertimeEdit) => {
        checkIfDataModified(setIsOvertimeEditMode, isOvertimeEdit)
    }

    const handleDefaultOvertimeChange = (evt) => {
        const message = evt
            ? <span>
                {t('If the Default Overtime setting is enabled for a company shift, any hours worked beyond the defined Normal Work Day will be classified as Default Overtime, provided those hours do not already fall within any specifically configured overtime ranges.')}
                <br /><br />
                {t('Additionally this feature is applicable only to employees who do not have Auto Flexitime enabled.')}
            </span>
            : t('Are you sure you want to clear the default overtime?');

        confirmAlert({
            title: t('Are you sure?'),
            message,
            buttons: [
                {
                    label: t('Confirm'),
                    onClick: () => setDefaultOvertimeId(evt ? evt.value : null)
                },
                { label: t('Cancel') }
            ]
        });
    }

    if (isLoading)
        return <LoadingSpinner />

    return (
        <>
            <Row className="my-4">
                <Col sm={3} xs={6}>
                    <label className="control-label">{t('Name')} <RequiredStar /></label>
                    <Input
                        placeholder={t('Enter name')}
                        value={shiftName}
                        onChange={(e) => setShiftName(e.target.value)}
                        disabled={isFCode}
                    />
                </Col>
                {(!isFCode && isStandardShift) && (
                    <Col sm={2} xs={6}>
                        <label className="control-label">{t('Associated with')}</label>
                        <Select
                            options={shiftType}
                            value={shiftType.find(item => item.value === associatedShift)}
                            onChange={evt => {
                                setAssociatedShift(evt.value)
                            }}
                            style={{ width: '100%' }}
                            disabled={isFCode}
                        />
                    </Col>
                )}
                {(!isStandardShift && !isFCode) && (
                    <Col sm={2} xs={6}>
                        <label className="control-label">{t('Work Type')} <RequiredStar /></label>
                        <Select
                            options={workTypes}
                            value={workTypes.find(item => item.value === workType)}
                            onChange={evt => setWorkType(evt.value)}
                            style={{ width: '100%' }}
                            disabled={isFCode}
                        />
                    </Col>
                )}
                <Col sm={2} xs={6}>
                    <label className="control-label">{t('Short Code')}</label>
                    <Input
                        placeholder={t('Eg. DS, AS, NS')}
                        value={shortShiftCode}
                        onChange={(e) => setShortShiftCode(e.target.value)}
                        disabled={isFCode}
                    />
                </Col>
                <Col sm={3} xs={6}>
                    <label className="control-label">{t('Pick Color')}</label>
                    <div className="d-flex align-items-center w-100 gap-2">
                        <Input
                            className="h-auto w-75"
                            placeholder={t('#b89494')}
                            value={shiftColor}
                            onChange={(e) => setShiftColor(e.target.value)}
                            disabled={isFCode || codeType === 'standbyCodes'}
                        />
                        <div
                            onClick={() => document.getElementById('colorPicker').click()}
                            style={{
                                width: "40px",
                                height: "25px",
                                backgroundColor: shiftColor,
                                borderRadius: "12px",
                                border: "1px solid #ccc",
                                cursor: "pointer",
                                boxShadow: "0 0 3px rgba(0,0,0,0.1)",
                            }}
                        />
                        <input
                            id="colorPicker"
                            type="color"
                            value={shiftColor}
                            onChange={(e) => setShiftColor(e.target.value)}
                            disabled={isFCode || codeType === 'standbyCodes'}
                            style={{
                                opacity: 0,
                                width: '1px'
                            }}
                        />
                    </div>
                    <label className="color-2550AC fs12">{t('Make sure to copy the color code, if you want to use it in another shift')}</label>
                </Col>
                <Col sm={2} xs={6}>
                    <label className="control-label">{t('Status')}</label>
                    <div className="d-flex align-items-center gap-2 mt-2 flex-column">
                        <SwitchWidget
                            value={isActive}
                            onChange={(checked) => setIsActive(checked)}
                        />
                        <span>{isActive ? t('Active') : t('Inactive')}</span>
                    </div>
                </Col>
            </Row>
            {isFCode && FCODE_RULES[shortShiftCode] && (
                <Row className="my-4">
                    <Col sm={12}>
                        <h3>{t(shortShiftCode)} — {t('Rules & Definition')}</h3>
                        <p className="text-muted mb-3">{t(FCODE_RULES[shortShiftCode].definition)}</p>
                        <ul className="list-unstyled">
                            {FCODE_RULES[shortShiftCode].rules.map(rule => (
                                <li key={rule.key} className="mb-2 d-flex align-items-start gap-2">
                                    <i className={`fa ${rule.icon} mt-1 font-color-primary`} />
                                    <span>{t(rule.text)}</span>
                                </li>
                            ))}
                        </ul>
                    </Col>
                </Row>
            )}
            {!isFCode && (
                <>
                    <h3>{t('Standard Hours')}
                        {isOvertimeEditMode &&
                            <OverlayTrigger
                                placement="right"
                                overlay={
                                    <Tooltip id="tooltip-top">
                                        {t("Enable Standard hours editing")}
                                    </Tooltip>
                                }
                            >
                                <i
                                    className="fa fa-edit ms-2 font-size-15 cursor-pointer font-color-primary"
                                    onClick={() => handleModeChange(false)}
                                />
                            </OverlayTrigger>
                        }
                    </h3>
                    <Row className="my-4">
                        <Col sm={2} xs={6}>
                            <label className="control-label">{t('Start time')}
                                <RequiredStar />
                                <span className="text-muted "><em> (hh:mm)</em></span>
                            </label>
                            <TimePickerInput
                                style={{ width: 150 }}
                                disabled={isOvertimeEditMode}
                                value={startTime}
                                format={dateTimeRules.time_format === "hh:mm" ? "HH:mm" : dateTimeRules.time_format}
                                onChange={(e) => { setStartTime(e) }}
                            />
                        </Col>
                        <Col sm={2} xs={6}>
                            <label className="control-label">{t('End time')} <RequiredStar />
                                <span className="text-muted "><em> (hh:mm)</em></span>
                            </label>
                            <TimePickerInput
                                style={{ width: 150 }}
                                disabled={isOvertimeEditMode}
                                value={endTime}
                                format={dateTimeRules.time_format === "hh:mm" ? "HH:mm" : dateTimeRules.time_format}
                                onChange={(e) => setEndTime(e)}
                            />
                        </Col>
                        {isStandardShift &&
                            (<Col sm={3} xs={6}>
                                <label className="control-label">{t('Break time')} <RequiredStar /></label>
                                <Select
                                    options={breakMinOptions}
                                    isDisabled={isOvertimeEditMode}
                                    value={breakMinOptions.find(item => item.value === breakTimeMinutes)}
                                    onChange={evt => {
                                        setBreakTimeMinutes(evt.value)
                                    }}
                                    style={{ width: '100%' }}
                                />
                            </Col>)
                        }
                        <Col sm={3} xs={6}>
                            <label className="control-label">{t('Normal Work Day')} <RequiredStar />
                                <span className="text-muted "><em> (hh:mm)</em></span>
                            </label>
                            <InputTime
                                width={"auto"}
                                disabled
                                value={normalWorkDay}
                            />
                        </Col>
                        {isStandardShift &&
                            (<Col sm={2} xs={6}>
                                <label className="control-label">{t('Buffer time')}
                                    <span className="text-muted "><em> (hh:mm)</em></span></label>
                                <InputTime
                                    width={"auto"}
                                    disabled={isOvertimeEditMode}
                                    value={bufferTime}
                                    onChange={(e) => setBufferTime(e)}
                                />
                            </Col>)
                        }
                        {!isFCode && !isStandardShift &&
                            (<Col sm={2} xs={6}>
                                <label className="control-label mb-1 d-flex justify-content-between">
                                    {t('Factor')}
                                    <span className="color-primary fw-600"><em>1:{factor} {`(${totalTimeFormat(Math.round(getMinutesFromTimeInput(normalWorkDay) / factor), dateTimeRules)}${t('hr')})`}</em></span>
                                </label>
                                <input type="range" min="1" max="10"
                                    onChange={event => setFactor(event.target.value)}
                                    value={factor}
                                />
                            </Col>)
                        }
                    </Row>
                    {!isFCode && !isStandardShift &&
                        <Row className="mb-4">
                            <Col sm={12} className="mb-3">
                                <WagetypeRefetch
                                    t={t}
                                    refetch={refetchWageType}
                                    lastUpdated={lastUpdated}
                                    isLoading={isLoadingWageTypes}
                                    dateTimeRules={dateTimeRules}
                                />
                            </Col>
                            <Col sm={4} xs={6}>
                                <label className="control-label">{`${t('Payroll')} ${t('Wage Type')}`}</label>
                                <Select
                                    options={wageType}
                                    value={payrollWageType && wageType.find(item => item.value === payrollWageType.value) || null}
                                    onChange={selectedItem => setPayrollWageType(selectedItem)}
                                    placeholder={`${t('Select')} ${t('Payroll')} ${t('Wage Type')}`}
                                    isClearable
                                    style={{ width: '100%' }}
                                />
                            </Col>
                        </Row>
                    }
                </>
            )}
            {!isFCode && isStandardShift && (
                <>
                    <h3>{t('Overtime Settings')}
                        {!isOvertimeEditMode &&
                            <OverlayTrigger
                                placement="right"
                                overlay={
                                    <Tooltip id="tooltip-top">
                                        {t("Enable Overtime Editing")}
                                    </Tooltip>
                                }
                            >
                                <i
                                    className="fa fa-edit ms-2 font-size-15 cursor-pointer font-color-primary"
                                    onClick={() => handleModeChange(true)}
                                />
                            </OverlayTrigger>
                        }
                    </h3>
                    <Row className="mb-5">
                        <Col sm={3} xs={5}>
                            <label className="control-label">{t('Maximum Works hours in day (Excluding Break)')} <RequiredStar />
                                <span className="text-muted "><em> (hh:mm)</em></span></label>
                            <InputTime
                                width={"auto"}
                                value={maximumWorkLimitInDay || normalWorkDay}
                                onChange={e => { setMaximumWorkLimitInDay(e) }}
                                disabled={!isOvertimeEditMode}
                            />
                        </Col>
                        <Col sm={3} xs={6}>
                            <label className="control-label">{t('Default Overtime')}</label>
                            <Select
                                options={overtimeSettingList}
                                value={overtimeSettingList.find(item => item.value === defaultOvertimeId) || null}
                                onChange={handleDefaultOvertimeChange}
                                placeholder={`${t('Select')} ${t('Overtime')}`}
                                isClearable
                                style={{ width: '100%' }}
                            />
                        </Col>
                        <Col sm={6} xs={12}>
                            <label className="control-label">{t('Overtime threshold')} <RequiredStar /></label>
                            <div className="slider-parent">
                                <input type="range" min="1" max="60"
                                    onChange={event => setOvertimeThreshold(event.target.value)}
                                    value={overtimeThreshold}
                                    disabled={!isOvertimeEditMode}
                                />
                                <div className="buble">
                                    {overtimeThreshold} {t('Minutes')}
                                </div>
                            </div>
                        </Col>
                        {val.id &&
                            <Col sm={12}>
                                <hr />
                                <Row className='mt-3 align-items-center'>
                                    <Col xs={8}>
                                        <label className="control-label">{t('Assigned Overtimes')} <RequiredStar /></label>
                                    </Col>
                                    <Col xs={4} className='d-flex justify-content-end'>
                                        <Button wd fill
                                            disabled={!isOvertimeEditMode}
                                            onClick={() => handleCompanyShiftOvertimeForm(null, true)}>
                                            <i className="fa fa-plus-circle fa-xl margin-right-10" />
                                        </Button>
                                    </Col>
                                </Row>
                                <TableWidget
                                    headers={['Overtime', 'From', 'To']}
                                    metaData={[
                                        { field: 'name', type: 'text' },
                                        { field: 'from', type: 'text' },
                                        { field: 'to', type: 'text' }
                                    ]}
                                    data={formatData(companyShiftOvertimes, dateTimeRules)}
                                    minRemoveIndex={0}
                                    onChange={_ => { }}
                                    page={'companyOvertimeSetting'}
                                    customDeleteHandler={handleCompanyShiftOvertimeDelete}
                                    customFormHandler={(index) => handleCompanyShiftOvertimeForm(index, false)}
                                />
                            </Col>
                        }
                    </Row>
                </>
            )}
            <div className="position-fixed" style={{ bottom: 10, backgroundColor: "white" }}>
                <Button wd me2 fill onClick={() => saveData(false)}>{t("Save")}</Button>
                <Button wd ms2 onClick={() => checkIfDataModified(cancel)}>{t("Cancel")}</Button>
            </div>
            {showOvertimeForm &&
                <OvertimeMapper
                    values={currentOvertimeSetting}
                    submit={(data) => handleCompanyShiftOvertime(data)}
                    cancel={() => handleCompanyShiftOvertimeForm(null, false)}
                    dateTimeRules={dateTimeRules}
                    t={t}
                    overtimeSettingList={overtimeSettingList}
                />
            }
        </>
    )
}

export default inject('commonStore', 'clientStore')(withRemoveDialog(AddShift));